import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, saveToken, clearToken } from "../lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user); setOrg(data.org);
      localStorage.setItem("aria_session", "1");
    } catch {
      setUser(null); setOrg(null);
      localStorage.removeItem("aria_session");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip /me check; AuthCallback exchanges session_id first.
    if (window.location.hash?.includes("session_id=")) { setLoading(false); return; }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    saveToken(data.token); setUser(data.user);
    await checkAuth();
    return data;
  };

  const signup = async (payload) => {
    const { data } = await api.post("/auth/signup", payload);
    saveToken(data.token); setUser(data.user); setOrg(data.org);
    await checkAuth();
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.warn("[ARIA] logout request failed", e); }
    clearToken(); setUser(null); setOrg(null);
    localStorage.removeItem("aria_session");
  };

  const refreshOrg = async () => {
    try {
      const { data } = await api.get("/settings/org");
      setOrg(data);
    } catch (e) { console.warn("[ARIA] refreshOrg failed", e); }
  };

  return (
    <AuthCtx.Provider value={{ user, org, loading, login, signup, logout, checkAuth, refreshOrg, setOrg }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
